<template>
  <div
    class="feedback-modal-overlay"
    role="dialog"
    aria-labelledby="feedback-modal-title"
    aria-modal="true"
    @click.self="$emit('close')"
    @keydown.esc.prevent="$emit('close')"
  >
    <div class="feedback-modal" @click.stop>
      <div class="feedback-modal-header">
        <h3 id="feedback-modal-title">
          {{
            feedbackModal.isSubmitted
              ? 'Thank you for your feedback'
              : 'Share feedback'
          }}
        </h3>
        <button
          aria-label="Close feedback"
          class="feedback-modal-close"
          type="button"
          @click="$emit('close')"
        >
          &#10005;
        </button>
      </div>

      <template v-if="feedbackModal.isSubmitted">
        <p class="feedback-success-message">
          Your feedback helps improve future AI responses.
        </p>
      </template>

      <template v-else>
        <div class="feedback-reasons" aria-label="Feedback reasons">
          <button
            v-for="reason in feedbackReasons"
            :key="reason.id"
            :class="[
              'feedback-reason-chip',
              { selected: feedbackModal.reasons.includes(reason.id) }
            ]"
            type="button"
            @click="$emit('toggle-reason', reason.id)"
          >
            {{ reason.label }}
          </button>
        </div>

        <textarea
          v-model.trim="commentProxy"
          class="feedback-details"
          placeholder="Share details (Optional)"
        ></textarea>

        <div v-if="feedbackModal.error" class="feedback-error">
          {{ feedbackModal.error }}
        </div>

        <div class="feedback-modal-footer">
          <button
            :disabled="feedbackModal.isSubmitting"
            class="feedback-submit-button"
            type="button"
            @click="$emit('submit')"
          >
            {{ feedbackModal.isSubmitting ? 'Submitting' : 'Submit' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FeedbackModal',
  props: {
    feedbackModal: {
      type: Object,
      required: true
    },
    feedbackReasons: {
      type: Array,
      required: true
    }
  },
  computed: {
    commentProxy: {
      get() {
        return this.feedbackModal.comment
      },
      set(comment) {
        this.$emit('update-comment', comment)
      }
    }
  }
}
</script>

<style scoped>
.feedback-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 20, 20, 0.35);
  overflow-y: auto;
}

.feedback-modal {
  width: min(100%, 620px);
  max-height: calc(100vh - 48px);
  padding: 22px 24px 24px;
  border: 1px solid #eef0f2;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 18px 44px rgba(20, 20, 20, 0.18);
  overflow-y: auto;
}

.feedback-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
}

.feedback-modal-header h3 {
  margin: 0;
  color: #050505;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
}

.feedback-modal-close {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  background: #ffffff;
  color: #050505;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.feedback-modal-close:hover {
  background: #f4f5f7;
}

.feedback-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.feedback-reason-chip {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #dedede;
  border-radius: 999px;
  background: #ffffff;
  color: #111111;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.15;
  cursor: pointer;
}

.feedback-reason-chip:hover {
  background: #f7f7f7;
}

.feedback-reason-chip.selected {
  border-color: #dcdcdc;
  background: #eeeeee;
  color: #5d5d5d;
  font-weight: 700;
}

.feedback-details {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-height: 126px;
  max-height: 240px;
  padding: 16px 18px;
  border: 1px solid #dedede;
  border-radius: 14px;
  color: #222222;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.45;
  resize: vertical;
  outline: none;
}

.feedback-details::placeholder {
  color: #858585;
}

.feedback-success-message {
  margin: -4px 0 0;
  color: #4b5566;
  font-size: 15px;
  line-height: 1.45;
}

.feedback-error {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #f0b5ae;
  border-radius: 8px;
  background: #fef0ed;
  color: #9a2f23;
  font-size: 13px;
  line-height: 1.35;
}

.feedback-modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.feedback-submit-button {
  min-width: 96px;
  height: 40px;
  padding: 0 20px;
  border: 2px solid #003f0a;
  border-radius: 10px;
  background: #00530d;
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

.feedback-submit-button:hover {
  background: #00440b;
}

.feedback-submit-button:disabled {
  cursor: default;
  opacity: 0.65;
}

@media (max-width: 640px) {
  .feedback-modal-overlay {
    align-items: flex-start;
    padding: 14px;
  }

  .feedback-modal {
    max-height: calc(100vh - 28px);
    padding: 18px;
    border-radius: 14px;
  }

  .feedback-modal-header h3 {
    font-size: 20px;
  }

  .feedback-reason-chip {
    min-height: 34px;
    font-size: 14px;
  }
}
</style>
